import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, Building2, DollarSign, Plus, Trash2, Loader2 } from "lucide-react";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import AccessRestricted from "../../components/AccessRestricted";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { MultiSelect } from "../../components/ui/multi-select";
import { JobPositionSelect } from "../../components/ui/job-position-select";
import { Combobox } from "../../components/ui/combobox";
import { useData } from "../../context/DataContext";
import api from "../../utils/api";
import { JobRole, JobPosition, QualificationPreset } from "../../types";

const URGENCY_OPTIONS = ["Low", "Medium", "High", "Critical"];

interface RequisitionData {
  id: number;
  urgency: string;
  startDate: string | null;
  businessJustification: string | null;
  salaryRangeMin: number | null;
  salaryRangeMax: number | null;
  keyDuties: string[];
  outcomes: string[];
  status: string;
  jobRole: { id: number; roleName: string } | null;
  jobPositions: { id: number; positionCode: string; location: string | null; status: string }[];
  department: { id: number; departmentName: string } | null;
  reportsTo: { id: number; name: string } | null;
  requiredCertificatesAndLicenses: { id: string; name: string }[];
  preferredQualifications: { id: string; name: string }[];
}

const EditRecruitmentRequest: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { jobRoles, departments, qualificationPresets, users } = useData();

  const [pageLoading, setPageLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [jobRoleChangedByUser, setJobRoleChangedByUser] = useState(false);

  const [selectedJobRoleId, setSelectedJobRoleId] = useState<string>("");
  const [selectedJobPositionIds, setSelectedJobPositionIds] = useState<string[]>([]);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string>("");
  const [urgency, setUrgency] = useState("Medium");
  const [startDate, setStartDate] = useState("");
  const [businessJustification, setBusinessJustification] = useState("");
  const [reportsToId, setReportsToId] = useState<string>("");
  const [minSalary, setMinSalary] = useState("");
  const [maxSalary, setMaxSalary] = useState("");

  const [requiredCertIds, setRequiredCertIds] = useState<string[]>([]);
  const [preferredQualIds, setPreferredQualIds] = useState<string[]>([]);
  const [keyDuties, setKeyDuties] = useState<string[]>([]);
  const [outcomes, setOutcomes] = useState<string[]>([]);

  const [jobPositions, setJobPositions] = useState<JobPosition[]>([]);
  const [jobPositionsLoading, setJobPositionsLoading] = useState(false);

  // Filter active job roles
  const activeJobRoles = useMemo(() => {
    return jobRoles.filter((jr) => !jr.archived);
  }, [jobRoles]);

  // Filter manager+ users for Reports To (exclude archived users)
  const managerUsers = useMemo(() => {
    return users.filter((u) =>
      !u.archived &&
      ["Admin", "Director", "Manager", "Senior Manager"].includes(u.userRole)
    );
  }, [users]);

  // Fetch requisition data on mount
  useEffect(() => {
    if (!id) return;
    setPageLoading(true);
    api
      .get<{ success: boolean; requisition: RequisitionData }>(`/api/requisitions/${id}`)
      .then((data) => {
        if (data.success) {
          const r = data.requisition;
          setUrgency(r.urgency || "Medium");
          setStartDate(r.startDate || "");
          setBusinessJustification(r.businessJustification || "");
          setReportsToId(r.reportsTo ? String(r.reportsTo.id) : "");
          setMinSalary(r.salaryRangeMin?.toString() || "");
          setMaxSalary(r.salaryRangeMax?.toString() || "");
          setKeyDuties(r.keyDuties?.length ? [...r.keyDuties] : []);
          setOutcomes(r.outcomes?.length ? [...r.outcomes] : []);
          setRequiredCertIds(r.requiredCertificatesAndLicenses?.map((c) => String(c.id)) || []);
          setPreferredQualIds(r.preferredQualifications?.map((q) => String(q.id)) || []);
          setSelectedDepartmentId(String(r.department?.id || ""));
          setSelectedJobPositionIds(r.jobPositions?.map((p) => String(p.id)) || []);
          setSelectedJobRoleId(String(r.jobRole?.id || ""));
        }
      })
      .catch((err) => {
        console.error("Failed to load requisition:", err);
        toast.error("Failed to load requisition");
      })
      .finally(() => setPageLoading(false));
  }, [id]);

  // Fetch job positions when job role changes
  useEffect(() => {
    const fetchPositions = async () => {
      if (!selectedJobRoleId) {
        setJobPositions([]);
        setSelectedJobPositionIds([]);
        return;
      }
      setJobPositionsLoading(true);
      try {
        const res = await api.get<{ success: boolean; positions: JobPosition[] }>(
          `/api/jobroles/${selectedJobRoleId}/positions`
        );
        if (res.success) {
          setJobPositions(res.positions);
        }
      } catch (error) {
        console.error("Error fetching positions:", error);
      } finally {
        setJobPositionsLoading(false);
      }
    };
    fetchPositions();
  }, [selectedJobRoleId]);

  // Auto-populate from selected job role (only when user manually changes it)
  useEffect(() => {
    if (!selectedJobRoleId) {
      setSelectedDepartmentId("");
      setMinSalary("");
      setMaxSalary("");
      setKeyDuties([]);
      setOutcomes([]);
      setRequiredCertIds([]);
      setPreferredQualIds([]);
      return;
    }
    if (!jobRoleChangedByUser) return;

    const role = activeJobRoles.find((jr) => String(jr.id) === selectedJobRoleId);
    if (role) {
      setSelectedDepartmentId(String(role.departmentId || ""));
      setMinSalary(role.salaryRangeMin?.toString() || "");
      setMaxSalary(role.salaryRangeMax?.toString() || "");
      setKeyDuties(role.keyDuties?.length ? [...role.keyDuties] : []);
      setOutcomes(role.outcomes?.length ? [...role.outcomes] : []);
      setRequiredCertIds(role.requiredCertificatesAndLicenses?.map((c: QualificationPreset) => String(c.id)) || []);
      setPreferredQualIds(role.preferredQualifications?.map((q: QualificationPreset) => String(q.id)) || []);
    }
  }, [selectedJobRoleId, activeJobRoles, jobRoleChangedByUser]);

  // --- Key Duties / Outcomes helpers ---
  const addDuty = () => setKeyDuties((prev) => [...prev, ""]);
  const removeDuty = (index: number) => setKeyDuties((prev) => prev.filter((_, i) => i !== index));
  const handleDutyChange = (index: number, value: string) => {
    setKeyDuties((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  };

  const addOutcome = () => setOutcomes((prev) => [...prev, ""]);
  const removeOutcome = (index: number) => setOutcomes((prev) => prev.filter((_, i) => i !== index));
  const handleOutcomeChange = (index: number, value: string) => {
    setOutcomes((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  };

  // --- Submit ---
  const handleSubmit = async () => {
    if (!id) return;
    if (!selectedJobRoleId) {
      toast.error("Please select a Job Role");
      return;
    }
    if (!selectedDepartmentId) {
      toast.error("Please select a Department");
      return;
    }
    if (!businessJustification.trim()) {
      toast.error("Business Justification is required");
      return;
    }
    if (minSalary && maxSalary && parseFloat(maxSalary) < parseFloat(minSalary)) {
      toast.error("Max salary must be greater than or equal to min salary");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        jobRoleId: parseInt(selectedJobRoleId),
        departmentId: parseInt(selectedDepartmentId),
        jobPositionIds: selectedJobPositionIds.map((id) => parseInt(id)),
        urgency,
        startDate: startDate || null,
        businessJustification: businessJustification.trim(),
        reportsToId: reportsToId ? parseInt(reportsToId) : null,
        salaryRangeMin: minSalary ? parseFloat(minSalary) : null,
        salaryRangeMax: maxSalary ? parseFloat(maxSalary) : null,
        keyDuties: keyDuties.filter((d) => d.trim() !== ""),
        outcomes: outcomes.filter((o) => o.trim() !== ""),
        requiredCertificatesAndLicenses: requiredCertIds.filter(Boolean),
        preferredQualifications: preferredQualIds.filter(Boolean),
      };

      const res = await api.put<{ success: boolean; requisition: any }>(
        `/api/requisitions/${id}`,
        payload
      );

      if (res.success) {
        toast.success("Requisition updated successfully");
        navigate(`/recruitment/requisitions/${id}`);
      }
    } catch (error: any) {
      console.error("Update requisition error:", error);
      toast.error(error.message || "Failed to update requisition");
    } finally {
      setLoading(false);
    }
  };

  // Selected job role object
  const selectedJobRole = activeJobRoles.find((jr) => String(jr.id) === selectedJobRoleId);

  // Selected department object
  const selectedDepartment = departments.find((d) => String(d.id) === selectedDepartmentId);

  // Job position options for JobPositionSelect (all positions, grouped by status)
  const jobPositionOptions = useMemo(() => {
    return jobPositions.map((p) => ({
      label: `${p.positionCode} - ${selectedJobRole?.title || "Unknown Role"}${p.location ? ` - ${p.location}` : ""}`,
      value: String(p.id),
      status: p.status as "Vacant" | "Filled",
    }));
  }, [jobPositions, selectedJobRole]);

  // Qualification preset options
  const qualPresetOptions = useMemo(() => {
    return qualificationPresets.map((q) => ({ label: q.name, value: String(q.id) }));
  }, [qualificationPresets]);

  if (pageLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 animate-in fade-in duration-500">
        <Loader2 size={32} className="animate-spin text-gray-400" />
        <p className="mt-3 text-sm font-medium text-gray-500">Loading requisition...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-6 animate-in fade-in duration-500 pb-10 px-0 sm:px-4 lg:px-0">
      {/* Back Button */}
      <button
        onClick={() => navigate(`/recruitment/requisitions/${id}`)}
        className="flex items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors w-fit"
      >
        <ChevronLeft size={18} />
        <span className="text-sm font-medium">Back to Requisition</span>
      </button>

      {/* Header */}
      <div className="flex items-center gap-3">
        <Building2 size={28} className="text-gray-900" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
            Edit Recruitment Request
          </h1>
          <p className="text-gray-500 text-sm font-medium">
            Update the position request details
          </p>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Position Details */}
          <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-6">
            <div>
              <h4 className="font-bold text-gray-900 text-base">Position Details</h4>
              <p className="text-gray-500 text-sm font-medium">Basic information about the role</p>
            </div>

            <div className="space-y-5">
              {/* Row 1: Job Role + Job Positions */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-sm font-bold text-gray-900">
                    Job Role <span className="text-red-500">*</span>
                  </label>
                  <Combobox
                    options={activeJobRoles.map((role) => ({ label: role.title, value: String(role.id) }))}
                    value={selectedJobRoleId}
                    onChange={(val) => {
                      setJobRoleChangedByUser(true);
                      setSelectedJobRoleId(val || "");
                    }}
                    placeholder="Select job role..."
                    searchPlaceholder="Search job role..."
                    emptyMessage="No job role found."
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-bold text-gray-900">Job Positions <span className="text-red-500">*</span></label>
                  <JobPositionSelect
                    options={jobPositionOptions}
                    selected={selectedJobPositionIds}
                    onChange={setSelectedJobPositionIds}
                    placeholder={
                      jobPositionsLoading
                        ? "Loading positions..."
                        : selectedJobRoleId
                        ? "Select positions..."
                        : "Select a job role first"
                    }
                    disabled={!selectedJobRoleId || jobPositionsLoading}
                  />
                </div>
              </div>

              {/* Row 2: Department */}
              <div className="space-y-1.5">
                <label className="text-sm font-bold text-gray-900">
                  Department <span className="text-red-500">*</span>
                </label>
                <Combobox
                  options={departments.map((dept) => ({ label: dept.departmentName, value: String(dept.id) }))}
                  value={selectedDepartmentId}
                  onChange={(val) => setSelectedDepartmentId(val || "")}
                  placeholder="Select department..."
                  searchPlaceholder="Search department..."
                  emptyMessage="No department found."
                />
                {selectedJobRole && jobRoleChangedByUser && (
                  <p className="text-xs text-gray-500 font-medium">
                    Auto-populated from Job Role. Can be overridden.
                  </p>
                )}
              </div>

              {/* Row 3: Urgency + Required Start Date */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-sm font-bold text-gray-900">Urgency</label>
                  <Select value={urgency} onValueChange={setUrgency}>
                    <SelectTrigger className="bg-white border-gray-200 rounded-xl h-11 text-sm font-medium focus:ring-2 focus:ring-blue-100">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl">
                      {URGENCY_OPTIONS.map((opt) => (
                        <SelectItem key={opt} value={opt}>
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-bold text-gray-900">Required Start Date</label>
                  <Input
                    type="date"
                    className="bg-white border-gray-200 rounded-xl h-11 text-sm font-medium focus:ring-2 focus:ring-blue-100"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
              </div>

              {/* Row 4: Business Justification */}
              <div className="space-y-1.5">
                <label className="text-sm font-bold text-gray-900">
                  Business Justification <span className="text-red-500">*</span>
                </label>
                <Textarea
                  placeholder="Explain why this position is needed..."
                  className="bg-white border-gray-200 rounded-xl text-sm font-medium min-h-[100px] resize-y focus:ring-2 focus:ring-blue-100"
                  value={businessJustification}
                  onChange={(e) => setBusinessJustification(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Salary Information */}
          <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-6">
            <div>
              <div className="flex items-center gap-2">
                <DollarSign size={18} className="text-gray-900" />
                <h4 className="font-bold text-gray-900 text-base">Salary Information</h4>
              </div>
              <p className="text-gray-500 text-sm font-medium mt-0.5">
                Inherited from Job Role, can be overridden
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-sm font-bold text-gray-900">Minimum Salary (AUD)</label>
                <Input
                  type="number"
                  className="bg-gray-50 border-gray-200 rounded-xl h-11 text-sm font-medium focus:ring-2 focus:ring-blue-100"
                  value={minSalary}
                  onChange={(e) => setMinSalary(e.target.value)}
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-bold text-gray-900">Maximum Salary (AUD)</label>
                <Input
                  type="number"
                  className="bg-gray-50 border-gray-200 rounded-xl h-11 text-sm font-medium focus:ring-2 focus:ring-blue-100"
                  value={maxSalary}
                  onChange={(e) => setMaxSalary(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Required Certificates, Licenses & Qualifications */}
          <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-6">
            <div>
              <h4 className="font-bold text-gray-900 text-base">
                Required Certificates, Licenses & Qualifications
              </h4>
              <p className="text-gray-500 text-sm font-medium mt-0.5">
                Essential qualifications for this role
              </p>
            </div>
            <MultiSelect
              options={qualPresetOptions}
              selected={requiredCertIds}
              onChange={setRequiredCertIds}
              placeholder="Select required qualifications..."
              maxDisplayTags={3}
            />
          </div>

          {/* Preferred Qualifications */}
          <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-6">
            <div>
              <h4 className="font-bold text-gray-900 text-base">Preferred Qualifications</h4>
              <p className="text-gray-500 text-sm font-medium mt-0.5">
                Nice-to-have qualifications that would be beneficial
              </p>
            </div>
            <MultiSelect
              options={qualPresetOptions}
              selected={preferredQualIds}
              onChange={setPreferredQualIds}
              placeholder="Select preferred qualifications..."
              maxDisplayTags={3}
            />
          </div>

          {/* Key Duties */}
          <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-6">
            <div>
              <h4 className="font-bold text-gray-900 text-base">Key Duties</h4>
              <p className="text-gray-500 text-sm font-medium mt-0.5">
                Main duties and responsibilities for this role
              </p>
            </div>
            <div className="space-y-3">
              {keyDuties.map((duty, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    placeholder="e.g. Develop and maintain web applications"
                    className="bg-gray-50 border-gray-200 rounded-xl h-11 text-sm font-medium focus:ring-2 focus:ring-blue-100"
                    value={duty}
                    onChange={(e) => handleDutyChange(index, e.target.value)}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeDuty(index)}
                    className="text-red-500 hover:text-red-700 shrink-0"
                    disabled={keyDuties.length === 1 && duty === ""}
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 flex items-center gap-2 rounded-xl h-10 text-sm font-medium"
                onClick={addDuty}
              >
                <Plus size={16} />
                <span>Add Responsibility</span>
              </Button>
            </div>
          </div>

          {/* Outcomes */}
          <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-6">
            <div>
              <h4 className="font-bold text-gray-900 text-base">Outcomes</h4>
              <p className="text-gray-500 text-sm font-medium mt-0.5">
                Expected outcomes and deliverables for this role
              </p>
            </div>
            <div className="space-y-3">
              {outcomes.map((outcome, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    placeholder="e.g. Successfully deliver 5+ projects per quarter"
                    className="bg-gray-50 border-gray-200 rounded-xl h-11 text-sm font-medium focus:ring-2 focus:ring-blue-100"
                    value={outcome}
                    onChange={(e) => handleOutcomeChange(index, e.target.value)}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeOutcome(index)}
                    className="text-red-500 hover:text-red-700 shrink-0"
                    disabled={outcomes.length === 1 && outcome === ""}
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 flex items-center gap-2 rounded-xl h-10 text-sm font-medium"
                onClick={addOutcome}
              >
                <Plus size={16} />
                <span>Add Outcome</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Right Column - Summary + Options */}
        <div className="lg:col-span-1 space-y-6">
          {/* Request Summary */}
          <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-4">
            <h4 className="font-bold text-gray-900 text-base">Request Summary</h4>

            <div className="space-y-3">
              <div>
                <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">Position</p>
                <p className="text-sm font-bold text-gray-900 mt-0.5">
                  {selectedJobRole?.title || "Not specified"}
                </p>
              </div>

              <div>
                <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">Department</p>
                <p className="text-sm font-bold text-gray-900 mt-0.5">
                  {selectedDepartment?.departmentName || "—"}
                </p>
              </div>

              <div>
                <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">
                  Positions Selected
                </p>
                <p className="text-sm font-bold text-gray-900 mt-0.5">
                  {selectedJobPositionIds.length} position{selectedJobPositionIds.length !== 1 ? "s" : ""}
                </p>
              </div>

              <div>
                <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">Urgency</p>
                <div className="mt-1">
                  <Badge
                    variant="outline"
                    className="rounded-lg border-0 font-bold px-2.5 py-1 text-[10px] bg-yellow-50 text-yellow-700"
                  >
                    {urgency}
                  </Badge>
                </div>
              </div>

              {(minSalary || maxSalary) && (
                <div>
                  <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">Salary Range</p>
                  <p className="text-sm font-bold text-gray-900 mt-0.5">
                    ${minSalary ? parseInt(minSalary).toLocaleString() : "—"} — $
                    {maxSalary ? parseInt(maxSalary).toLocaleString() : "—"}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Additional Options */}
          <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-4">
            <h4 className="font-bold text-gray-900 text-base">Additional Options</h4>

            <div className="space-y-1.5">
              <label className="text-sm font-bold text-gray-900">Reports To</label>
              <Combobox
                options={managerUsers.map((user) => ({ label: `${user.name} (${user.userRole})`, value: String(user.id) }))}
                value={reportsToId}
                onChange={(val) => setReportsToId(val || "")}
                placeholder="Select manager..."
                searchPlaceholder="Search manager..."
                emptyMessage="No manager found."
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button
              className="w-full bg-slate-950 hover:bg-slate-900 text-white flex items-center justify-center gap-2 h-11 rounded-xl text-sm font-bold"
              onClick={handleSubmit}
              disabled={loading}
            >
              <Plus size={16} />
              <span>{loading ? "Updating..." : "Update Requisition"}</span>
            </Button>
            <Button
              variant="outline"
              className="w-full bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-11 rounded-xl text-sm font-bold"
              onClick={() => navigate(`/recruitment/requisitions/${id}`)}
              disabled={loading}
            >
              <span>Cancel</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditRecruitmentRequest;
